/**
 * Preferences, backed up to the signed-in account.
 *
 * The server is a roaming copy of localStorage, not a second source of truth.
 * Preferences are read synchronously during render (useUserSettings) and even
 * before React exists (the ThemeScript in __root.tsx), so everything here
 * writes *through* services/user.service.ts rather than introducing a store of
 * its own — that is also what makes the listener fire, which is the only thing
 * that invalidates useUserSettings' cached snapshot.
 *
 * IO stays out of the store, matching favorites.service.ts and auth-log.ts.
 */

import { authedFetch } from 'api/authed-fetch'
import type { PrefsResponse, PrefsPutBody } from '../../shared/prefs-contract'
import { UserPrefs } from 'models/user.model'
import { validateUserPrefs } from 'lib/validateUserPrefs'
import defaultUserPrefs from 'lib/defaultUserPrefs'
import {
   applyRemoteUserSettings,
   getUserPrefsUid,
   getUserPrefsUpdatedAt,
   getUserSettings,
   markUserSettingsAccount,
} from './user.service'

export type Reconciliation = 'adopt-remote' | 'push-local'

type ReconcileInput = {
   local: UserPrefs
   localUpdatedAt: string | null
   localUid: string | null
   uid: string
   remote: UserPrefs | null
   remoteUpdatedAt: string | null
}

const isUntouched = (prefs: UserPrefs, updatedAt: string | null): boolean =>
   !updatedAt &&
   prefs.translitSchemeName === defaultUserPrefs.translitSchemeName &&
   prefs.toneMarkers === defaultUserPrefs.toneMarkers &&
   prefs.fontSize === defaultUserPrefs.fontSize &&
   prefs.theme === defaultUserPrefs.theme

/**
 * Which side wins when a sign-in finds preferences on both.
 *
 * The rule is **local wins, unless local has never been touched by this
 * account**. Server-wins would be wrong: a sign-in already seeds the account
 * row with the defaults (see auth.sign-in.ts), so a GET almost always returns
 * *something*, and somebody who has read RTGS for two years would be silently
 * reset the first time they signed in.
 *
 * Kept pure and exported so the decision can be tested without a network.
 */
export function chooseReconciliation(input: ReconcileInput): Reconciliation {
   const { local, localUpdatedAt, localUid, uid, remote } = input

   // Nothing stored for the account, or a row that failed to parse — either way
   // there is nothing to adopt.
   if (!remote) return 'push-local'

   // Somebody else was signed in here. Whatever is in localStorage is their
   // preference, not this account's — the direct analogue of the uid-keying in
   // useFavoritesSync.
   if (localUid !== null && localUid !== uid) return 'adopt-remote'

   // This browser has never expressed a preference, so there is nothing to
   // lose by taking the account's. The case the feature exists for: a new
   // device, or an account whose settings came across in the legacy Firestore
   // import.
   if (isUntouched(local, localUpdatedAt)) return 'adopt-remote'

   // Both sides have been touched, and there is no honest way to tell which is
   // newer: users.updated_at is bumped by every sign-in, so it means "this row
   // was touched", not "these preferences changed", and the server would always
   // look newer. Until the row carries a prefs-specific timestamp, local wins.
   // Changing the page under someone who is looking at it is the shocking
   // failure; pushing their choice up instead is always recoverable.
   return 'push-local'
}

const samePrefs = (a: UserPrefs, b: UserPrefs): boolean =>
   a.translitSchemeName === b.translitSchemeName &&
   a.toneMarkers === b.toneMarkers &&
   a.fontSize === b.fontSize &&
   a.theme === b.theme

export async function fetchRemotePrefs(): Promise<
   { ok: true; prefs: UserPrefs | null; updatedAt: string | null } | { ok: false }
> {
   const result = await authedFetch<PrefsResponse>('/api/prefs', {}, 'prefs')
   if (!result.ok) return { ok: false }

   // parsePrefs hands back loose strings, and this has been over a network —
   // narrow it before anything can render from it.
   const prefs = result.data.prefs ? validateUserPrefs(result.data.prefs) : null
   return { ok: true, prefs, updatedAt: result.data.updatedAt ?? null }
}

export async function pushLocalPrefs(prefs: UserPrefs): Promise<boolean> {
   const body: PrefsPutBody = { prefs }
   const result = await authedFetch<{ ok: true }>(
      '/api/prefs',
      { method: 'PUT', body: JSON.stringify(body) },
      'prefs'
   )
   return result.ok
}

/**
 * The last preferences this browser sent up, so the listener below can tell a
 * genuine local edit from the echo of one it just applied from the server.
 */
let lastSyncedJson: string | null = null

export function markPrefsSynced(prefs: UserPrefs): void {
   lastSyncedJson = JSON.stringify(prefs)
}

export function needsPush(prefs: UserPrefs): boolean {
   return JSON.stringify(prefs) !== lastSyncedJson
}

/** Only for tests — a module-level cache would otherwise leak between them. */
export function resetPrefsSyncState(): void {
   lastSyncedJson = null
}

export async function reconcilePrefs(uid: string): Promise<void> {
   const remote = await fetchRemotePrefs()
   if (!remote.ok) return

   const local = getUserSettings()
   const decision = chooseReconciliation({
      local,
      localUpdatedAt: getUserPrefsUpdatedAt(),
      localUid: getUserPrefsUid(),
      uid,
      remote: remote.prefs,
      remoteUpdatedAt: remote.updatedAt,
   })

   if (decision === 'adopt-remote' && remote.prefs) {
      markPrefsSynced(remote.prefs)
      applyRemoteUserSettings(remote.prefs, remote.updatedAt, uid)
      return
   }

   markUserSettingsAccount(uid)
   if (decision === 'push-local') {
      // The account already agrees; no need to spend a write saying so.
      if (remote.prefs && samePrefs(remote.prefs, local)) {
         markPrefsSynced(local)
         return
      }
      if (await pushLocalPrefs(local)) markPrefsSynced(local)
   }
}
